class PremiumProcessTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.tabs = Array.from(this.querySelectorAll('[data-process-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-process-panel]'));
    this.steps = Array.from(this.querySelectorAll('[data-process-step]'));
    this.visibleSteps = new Set();
    this.videoSelectionFrame = null;
    this.status = this.querySelector('[data-process-status]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isVisible = !('IntersectionObserver' in window);
    this.playbackEnabled = this.isVisible;
    this.playbackController = null;
    this.playbackPanel = null;

    this.onTabClick = this.handleTabClick.bind(this);
    this.onTabKeydown = this.handleTabKeydown.bind(this);
    this.onBlockSelect = this.handleBlockSelect.bind(this);
    this.onRouteResize = this.scheduleMobileRoutes.bind(this);
    window.addEventListener('resize', this.onRouteResize, { passive: true });
    if ('ResizeObserver' in window) {
      this.routeObserver = new ResizeObserver(this.onRouteResize);
      this.routeObserver.observe(this);
    }

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onTabKeydown);
    });
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    if ('IntersectionObserver' in window) {
      // Do not wake video media while the process section is still below the
      // viewport. The active batch starts loading only when the user reaches it.
      this.observer = new IntersectionObserver(this.handleIntersection, { rootMargin: '0px', threshold: 0 });
      this.observer.observe(this);
      this.dataset.stepObserver = 'true';
      this.stepObserver = new IntersectionObserver(this.handleStepIntersection, {
        rootMargin: '160px 0px 160px 0px',
        threshold: 0,
      });
    } else {
      this.dataset.inView = 'true';
      this.steps.forEach((step) => {
        step.dataset.stepRevealed = 'true';
        step.dataset.stepVisible = 'true';
      });
    }

    const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    if (selected) this.activate(selected.dataset.processTab, { focus: false, announce: false, initial: true });
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick);
      tab.removeEventListener('keydown', this.onTabKeydown);
    });
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.observer?.disconnect();
    this.stepObserver?.disconnect();
    this.routeObserver?.disconnect();
    window.removeEventListener('resize', this.onRouteResize);
    cancelAnimationFrame(this.routeFrame);
    cancelAnimationFrame(this.videoSelectionFrame);
    cancelAnimationFrame(this.processStartFrame);
    this.playbackController?.abort();
    this.playbackPanel = null;
    this.pauseAllVideos({ release: true });
    this.initialized = false;
  }

  handleTabClick(event) {
    this.activate(event.currentTarget.dataset.processTab, { focus: false, announce: true, resetToStart: true });
  }

  handleTabKeydown(event) {
    const current = this.tabs.indexOf(event.currentTarget);
    let next = current;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % this.tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    else return;

    event.preventDefault();
    const tab = this.tabs[next];
    this.activate(tab.dataset.processTab, { focus: true, announce: true, resetToStart: true });
  }

  activate(group, options = {}) {
    const { focus = false, announce = false, initial = false, resetToStart = false } = options;
    const activeTab = this.tabs.find((tab) => tab.dataset.processTab === group);
    const activePanel = this.ensureProcessPanel(group);
    if (!activeTab || !activePanel) return;

    if (!initial) this.playbackEnabled = true;
    if (this.playbackPanel !== activePanel) {
      this.playbackController?.abort();
      this.playbackPanel = null;
    }

    this.panels = Array.from(this.querySelectorAll('[data-process-panel]'));
    this.steps = Array.from(this.querySelectorAll('[data-process-step]'));

    this.tabs.forEach((tab) => {
      const selected = tab === activeTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      if (panel === activePanel) return;
      panel.classList.remove('is-active');
      panel.hidden = true;
      this.pausePanelVideos(panel);
    });

    activePanel.hidden = false;
    // Keep the selected panel fully active throughout a tab change. Removing
    // this class for two frames made every card fade to opacity: 0 while its
    // videos were being initialized, which looked like a black brightness
    // overlay—most noticeably in the Wood-Pressed Oil process.
    activePanel.classList.add('is-active');

    if (focus) activeTab.focus({ preventScroll: true });
    if (!initial && !resetToStart) {
      activeTab.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    }
    this.activeGroup = group;
    this.observePanelSteps(activePanel);
    this.scheduleMobileRoutes();
    if (resetToStart && !initial) this.scrollProcessToStart(activePanel);

    if (this.playbackEnabled) this.activatePanelVideos(activePanel);

    if (announce && this.status) {
      const label = activeTab.textContent.trim();
      const count = activePanel.querySelectorAll('[data-process-step]').length;
      this.status.textContent = `${label}: ${count} process ${count === 1 ? 'step' : 'steps'}`;
    }
  }

  scrollProcessToStart(panel) {
    cancelAnimationFrame(this.processStartFrame);
    this.processStartFrame = requestAnimationFrame(() => {
      // Only explicit tab navigation moves the page. A newer selection cancels
      // this frame; video buffering and initial page load never trigger it.
      if (!this.isConnected || panel.hidden || panel.dataset.processPanel !== this.activeGroup) return;
      const firstStep = panel.querySelector('[data-process-step]');
      if (!firstStep) return;

      firstStep.dataset.stepRevealed = 'true';
      const tabs = this.querySelector('.ppts__tabs-scroll');
      const headerHeight = parseFloat(getComputedStyle(this).getPropertyValue('--header-height')) || 0;
      const tabsStyle = tabs && getComputedStyle(tabs);
      const offset = tabsStyle?.position === 'sticky'
        ? (parseFloat(tabsStyle.top) || 0) + tabs.getBoundingClientRect().height
        : headerHeight;
      // The list has no entrance transform, so the target stays accurate while
      // the first card's existing reveal animation runs.
      // The theme can scroll inside .page-wrapper rather than the window.
      // Find the actual scrollport so both layouts return to the first card.
      let scroller = this.parentElement;
      while (scroller) {
        if (/(auto|scroll)/.test(getComputedStyle(scroller).overflowY) && scroller.scrollHeight > scroller.clientHeight) break;
        scroller = scroller.parentElement;
      }
      const scrollportTop = scroller ? scroller.getBoundingClientRect().top : 0;
      const start = firstStep.parentElement.getBoundingClientRect().top - scrollportTop + (scroller ? scroller.scrollTop : window.scrollY);
      (scroller || window).scrollTo({
        top: Math.max(0, start - offset - 12),
        behavior: this.reducedMotion.matches ? 'instant' : 'smooth',
      });
    });
  }

  ensureProcessPanel(group) {
    const existingPanel = this.querySelector(`[data-process-panel="${group}"]`);
    if (existingPanel) return existingPanel;

    const template = this.querySelector(`template[data-process-panel-template="${group}"]`);
    if (!template) return null;

    const fragment = template.content.cloneNode(true);
    const panel = fragment.querySelector(`[data-process-panel="${group}"]`);
    if (!panel) return null;

    template.replaceWith(fragment);
    return panel;
  }

  async activatePanelVideos(panel) {
    if (!panel || panel.hidden || !this.playbackEnabled || this.playbackPanel === panel) return;
    this.playbackController?.abort();
    const controller = new AbortController();
    this.playbackController = controller;
    this.playbackPanel = panel;
    const videos = Array.from(panel.querySelectorAll('video'));

    // Load the complete selected batch before issuing its play requests.
    // Inactive batches never receive source URLs or preload requests.
    const ready = videos.map((video) => {
      this.ensureVideoPoster(video);
      video.autoplay = false;
      video.loop = true;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.removeAttribute('loading');
      video.preload = 'auto';
      const readiness = this.waitForVideo(video, controller.signal);
      this.ensureVideoLoaded(video);
      return readiness;
    });

    const playable = await Promise.all(ready);
    if (controller.signal.aborted || this.playbackPanel !== panel || panel.hidden) return;
    // Issue every play request in the same task after the batch has buffered.
    videos.forEach((video, index) => {
      if (!playable[index] || !this.canActivateVideo(video)) return;
      if (video.readyState >= 1) video.currentTime = 0;
      video.autoplay = true;
      video.play()?.catch(() => {});
    });
  }

  waitForVideo(video, signal) {
    if (signal.aborted) return Promise.resolve(false);
    if (video.readyState >= 2) return Promise.resolve(true);
    return new Promise((resolve) => {
      const finish = (playable) => {
        clearTimeout(timeout);
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('canplay', onReady);
        video.removeEventListener('error', onError);
        signal.removeEventListener('abort', onAbort);
        resolve(playable);
      };
      const onReady = () => finish(true);
      const onError = () => finish(false);
      const onAbort = () => finish(false);
      // A slow or broken clip must not hold back the rest of the batch forever.
      // play() can continue buffering a slow clip after this fallback.
      const timeout = setTimeout(() => finish(true), 10000);
      video.addEventListener('loadeddata', onReady);
      video.addEventListener('canplay', onReady);
      video.addEventListener('error', onError);
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  deactivateStepVideos(step, { release = false } = {}) {
    step?.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.autoplay = false;
      video.preload = 'none';
      if (release) this.releaseVideo(video);
    });
  }

  scheduleActiveStepVideo() {
    cancelAnimationFrame(this.videoSelectionFrame);
    this.videoSelectionFrame = requestAnimationFrame(() => this.updateActiveStepVideo());
  }

  updateActiveStepVideo() {
    const panel = this.panels.find((item) => item.dataset.processPanel === this.activeGroup);
    if (this.playbackEnabled && panel && !panel.hidden) this.activatePanelVideos(panel);
  }

  observePanelSteps(panel) {
    if (!panel) return;

    this.stepObserver?.disconnect();
    const steps = panel.querySelectorAll('[data-process-step]');

    if (this.stepObserver) {
      steps.forEach((step) => this.stepObserver.observe(step));
      return;
    }

    steps.forEach((step) => {
      step.dataset.stepRevealed = 'true';
      step.dataset.stepVisible = 'true';
    });
  }

  ensureVideoLoaded(video) {
    // A source URL is assigned only to a video inside the currently selected,
    // visible batch. Inactive process templates therefore stay network-inert.
    if (!this.canActivateVideo(video)) return;
    if (video.dataset.sourcesLoaded === 'true') return;

    let hasSource = false;
    if (video.dataset.src) {
      video.src = video.dataset.src;
      delete video.dataset.src;
      hasSource = true;
    }
    video.querySelectorAll('source[data-src]').forEach((source) => {
      const originalSource = source.dataset.originalSrc || source.dataset.src;
      source.dataset.originalSrc = originalSource;
      source.src = originalSource;
      source.removeAttribute('data-src');
      hasSource = true;
    });

    if (hasSource) {
      video.dataset.sourcesLoaded = 'true';
      video.load();
    }
  }

  canActivateVideo(video) {
    const panel = video.closest('[data-process-panel]');
    return Boolean(
      this.playbackEnabled &&
        panel &&
        !panel.hidden &&
        panel.dataset.processPanel === this.activeGroup
    );
  }

  ensureVideoPoster(video) {
    const poster = video.dataset.originalPoster || video.dataset.poster || video.getAttribute('poster');
    if (poster) {
      video.dataset.originalPoster = poster;
      const optimizedPoster = this.getOptimizedPosterSource(poster);
      if (video.poster !== optimizedPoster) video.poster = optimizedPoster;
      delete video.dataset.poster;
    }

    const fallback = video.querySelector('img[data-src]');
    if (fallback) {
      fallback.src = this.getOptimizedPosterSource(fallback.dataset.src);
      fallback.removeAttribute('data-src');
    }
  }

  prepareStepPosters(step) {
    step?.querySelectorAll('video').forEach((video) => this.ensureVideoPoster(video));
  }

  getOptimizedPosterSource(sourceUrl) {
    if (!sourceUrl || !/_small\.jpg/.test(sourceUrl)) return sourceUrl;
    return sourceUrl.replace(/_small\.jpg/, window.matchMedia('(max-width: 749px)').matches ? '_medium.jpg' : '_large.jpg');
  }

  releaseVideo(video) {
    video.removeAttribute('autoplay');
    video.preload = 'none';
    let released = false;
    if (video.hasAttribute('src')) {
      video.dataset.src = video.getAttribute('src');
      video.removeAttribute('src');
      released = true;
    }
    video.querySelectorAll('source[src]').forEach((source) => {
      source.dataset.src = source.dataset.originalSrc || source.getAttribute('src');
      source.removeAttribute('src');
      released = true;
    });
    if (!released) return;
    video.dataset.sourcesLoaded = 'false';
    video.load();
  }

  pausePanelVideos(panel) {
    panel.querySelectorAll('[data-process-step]').forEach((step) => this.deactivateStepVideos(step, { release: true }));
  }

  pauseAllVideos({ release = false } = {}) {
    this.steps.forEach((step) => this.deactivateStepVideos(step, { release }));
  }

  handleBlockSelect(event) {
    if (event.detail?.load) return;

    const block = event.target.closest?.('[data-process-group]');
    if (!block || !this.contains(block)) return;
    const group = block.dataset.processGroup;
    if (group) this.activate(group, { focus: false, announce: false });
  }

  handleIntersection = (entries) => {
    this.isVisible = entries[0]?.isIntersecting ?? true;
    if (this.isVisible) {
      this.playbackEnabled = true;
      this.dataset.inView = 'true';
      const panel = this.panels.find((item) => item.dataset.processPanel === this.activeGroup);
      if (panel && !this.stepObserver) {
        this.visibleSteps = new Set(panel.querySelectorAll('[data-process-step]'));
        this.scheduleActiveStepVideo();
      } else if (panel) {
        this.scheduleActiveStepVideo();
      }
    }
  };

  handleStepIntersection = (entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      target.dataset.stepVisible = String(isIntersecting);
      if (isIntersecting) {
        this.visibleSteps.add(target);
        this.prepareStepPosters(target);
        target.dataset.stepRevealed = 'true';
      } else {
        this.visibleSteps.delete(target);
      }
    });
  };

  scheduleMobileRoutes() {
    cancelAnimationFrame(this.routeFrame);
    this.routeFrame = requestAnimationFrame(() => this.updateMobileRoutes());
  }

  updateMobileRoutes() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const panel = this.panels.find((item) => item.dataset.processPanel === this.activeGroup);
    if (!panel || panel.hidden) return;
    const steps = Array.from(panel.querySelectorAll('[data-process-step]'));

    steps.forEach((step, index) => {
      const next = steps[index + 1];
      const route = step.querySelector('.ppts__route');
      const svg = route?.querySelector('.ppts__mobile-route-line');
      const media = step.querySelector('.ppts__media');
      const nextMedia = next?.querySelector('.ppts__media');
      if (!svg || !media || !nextMedia) return;

      // Layout offsets exclude the entrance transforms, so the curve stays
      // attached as each step reveals and when a phone changes orientation.
      const width = route.clientWidth;
      const height = route.clientHeight;
      if (!width || !height) return;
      const start = media.offsetLeft + media.offsetWidth / 2;
      const end = next.offsetLeft - step.offsetLeft + nextMedia.offsetLeft + nextMedia.offsetWidth / 2;
      const bend = Math.min(parseFloat(getComputedStyle(this).getPropertyValue('--ppts-route-width')) || 85, width / 3);
      const sameSide = Math.abs(end - start) < 8;
      const direction = start < width / 2 ? 1 : -1;
      const control1 = sameSide ? start + direction * bend : start;
      const control2 = sameSide ? end + direction * bend : end;
      const path = `M${start} 0 C${control1} ${height * 0.56} ${control2} ${height * 0.44} ${end} ${height}`;
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      svg.querySelectorAll('path').forEach((line) => line.setAttribute('d', path));
      svg.querySelector('.ppts__mobile-route-spark').style.offsetPath = `path('${path}')`;
      route.style.setProperty('--ppts-route-start-x', `${start}px`);
    });
  }
}

if (!customElements.get('premium-process-tabs')) {
  customElements.define('premium-process-tabs', PremiumProcessTabs);
}
